// Direct imports
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "./tracebehavior";
import { RuntimeToggleLatch, type RuntimeToggleable } from "./runtimeToggleable";
import { BillboardData } from "../scenedata/behaviors/billboarddata";
import { pivotOnParentScale } from "../utils/TransformUtils";

// Shared across all Billboard instances — safe because observers run synchronously.
const _tmpWorldQuat = new Quaternion();
const _tmpParentQuat = new Quaternion();
const _tmpParentInv = new Quaternion();
const _tmpTargetLocal = new Quaternion();
const _tmpRestForward = new Vector3();
const _tmpParentScale = new Vector3();
// Read-only identity for slerp sources — never mutated.
const _identityQuat = Quaternion.Identity();

/** Return-to-rest settle threshold on |quaternion dot| (cos of the half
 *  angle): 1 − 1e-5 ≈ within half a degree of the rest orientation. */
const SETTLE_EPSILON = 1e-5;

/**
 * Billboard behavior - Makes an object's +Z face the camera.
 *
 * Computes a world-space facing orientation each frame, then converts it
 * to the animation root's local space by removing the parent's world
 * rotation, and pivots the root on the parent's scale so a non-uniform
 * scale doesn't shear the turn. This ensures correct results even when the
 * SceneObject's transform has a non-identity rotation or a non-uniform scale.
 *
 * Tuning (all optional, defaults reproduce the classic hard billboard):
 * `axes` constrains which rotation axes track; `influence` makes partial
 * turns; `damping` adds eased, lagged turning; `viewAngle` + `onExit`
 * disengage tracking beyond an angular cone around the authored resting
 * direction (ease back or hold).
 *
 * RuntimeToggleable: the graph can switch facing on/off at play time
 * (trace/setBehaviorEnabled). Disabling disengages tracking exactly like
 * leaving the view cone — `onExit` decides ease-back vs hold. The authored
 * `enabled` field is the state at every play-mode start.
 */
export class Billboard extends TraceBehavior<BillboardData> implements RuntimeToggleable {
    static readonly behaviorType = "Billboard" as const;

    private _observer: Observer<Scene> | null = null;
    private _lastFrameTime: number | null = null;
    /** Runtime on/off + the Scene-Starts play-start latch (see RuntimeToggleLatch). */
    private readonly _toggle = new RuntimeToggleLatch();
    /** Disengaged return-to-rest has completed — stop writing to the node
     *  each frame so other systems can rotate it. Cleared on re-engage. */
    private _settled = false;
    /** Billboard has written the node's rotation since play start or its
     *  last settle, so the node's pivot is Billboard's to keep. */
    private _holdsTurn = false;

    setRuntimeEnabled(enabled: boolean): void {
        // No clock reset on toggle (return value unused): the damped slerp
        // continues smoothly and `onExit` governs the disengage transition.
        this._toggle.set(enabled, this._observer !== null);
    }

    isRuntimeEnabled(): boolean {
        return this._toggle.enabled;
    }

    load(): void {
        // Ensure animation root exists so billboard doesn't affect world transform
        this.sceneObject.getOrCreateAnimationRoot();
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            this._applyBillboard();
        } else {
            this._removeBillboard();
        }
    }

    protected onDetach(): void {
        this._removeBillboard();
    }

    protected async applyCommit(_patch: Partial<BillboardData>): Promise<void> {
        this._removeBillboard();
        if (this.playMode) {
            this._applyBillboard();
        }
    }

    private _applyBillboard(): void {
        const animRoot = this.sceneObject.animationRoot;
        if (!animRoot) return;

        const scene = this.sceneObject.babylonScene;

        // Use rotationQuaternion for manual billboard
        if (!animRoot.rotationQuaternion) {
            animRoot.rotationQuaternion = Quaternion.Identity();
        }
        this._lastFrameTime = null;
        this._settled = false;
        this._holdsTurn = false;
        // Every play start resets the runtime toggle to the authored state —
        // unless an enable already landed this play-start window (Scene
        // Starts trigger), which the reset consumes rather than stomps
        this._toggle.reset(this._resolved.enabled ?? true);

        this._observer = scene.onBeforeRenderObservable.add(() => {
            const camera = scene.activeCamera;
            if (!camera || !animRoot.rotationQuaternion) return;

            const r = this._resolved;
            // "y" = turn (yaw), "x" = tilt (pitch); "z" (banking) must be
            // ignored — roll stays 0. Omitted axes = all three (the classic
            // full billboard, matching the schema default).
            const axes = r.axes ?? ["x", "y", "z"];
            const yawOn = axes.includes("y");
            const pitchOn = axes.includes("x");

            const camPos = camera.globalPosition;
            const parent = animRoot.parent as TransformNode | null;
            const nodePos = parent
                ? parent.getAbsolutePosition()
                : animRoot.getAbsolutePosition();

            // While Billboard holds a turn it wrote, every exit below refreshes
            // the pivot from this scale, so the turn stays rigid as the scale
            // changes. Otherwise the root's pivot is left to other systems.
            if (parent) {
                parent.getWorldMatrix().decompose(_tmpParentScale, _tmpParentQuat, undefined);
            } else {
                _tmpParentScale.setAll(1);
                _tmpParentQuat.copyFrom(_identityQuat);
            }

            // Direction from node toward camera
            const dir = camPos.subtract(nodePos);

            // Skip update if camera is closer than minDistance
            const minDist = r.minDistance ?? 0;
            if (minDist > 0 && dir.lengthSquared() < minDist * minDist) {
                if (this._holdsTurn) pivotOnParentScale(animRoot, _tmpParentScale);
                return;
            }

            // Reverse: face away from camera
            if (r.reverse) {
                dir.negateInPlace();
            }

            const len = dir.length();
            if (len < 1e-6) {
                if (this._holdsTurn) pivotOnParentScale(animRoot, _tmpParentScale);
                return;
            }
            dir.scaleInPlace(1 / len);

            // View cone: tracking engages only while the camera sits within
            // `viewAngle` degrees (the cone half-angle) of the authored
            // resting direction (the parent-space +Z, since the rest pose of
            // the animation root is identity). Unlike a cursor target, the
            // camera never leaves the scene — this cone is what creates "out
            // of range" at all. 0 = unlimited; >= 180 also unlimited (the
            // cone covers everything), which is why the test below skips both.
            // A runtime disable (trace/setBehaviorEnabled) disengages the same
            // way, so `onExit` governs both.
            let engaged = this._toggle.enabled;
            const viewAngle = r.viewAngle ?? 0;
            if (engaged && viewAngle > 0 && viewAngle < 180) {
                _tmpRestForward.set(0, 0, 1);
                _tmpRestForward.applyRotationQuaternionToRef(_tmpParentQuat, _tmpRestForward);
                const cos = Math.min(1, Math.max(-1, Vector3.Dot(_tmpRestForward, dir)));
                engaged = (Math.acos(cos) * 180) / Math.PI <= viewAngle;
            }

            if (engaged) {
                this._settled = false;
                // Facing target from the axis mask. Yaw+pitch with zero roll is
                // exactly the classic cross-product billboard, but stays stable
                // when the camera passes directly overhead.
                const yaw = yawOn ? Math.atan2(dir.x, dir.z) : 0;
                const pitch = pitchOn
                    ? Math.atan2(-dir.y, Math.sqrt(dir.x * dir.x + dir.z * dir.z))
                    : 0;
                Quaternion.RotationYawPitchRollToRef(yaw, pitch, 0, _tmpWorldQuat);

                // Convert world-space quaternion to local space:
                // local = inverse(parentWorld) * world
                if (parent) {
                    _tmpParentInv.copyFrom(_tmpParentQuat).invertInPlace();
                    _tmpParentInv.multiplyToRef(_tmpWorldQuat, _tmpTargetLocal);
                } else {
                    _tmpTargetLocal.copyFrom(_tmpWorldQuat);
                }

                // Influence: partial turn from the rest orientation toward the target
                const influence = r.influence ?? 1;
                if (influence < 1) {
                    Quaternion.SlerpToRef(_identityQuat, _tmpTargetLocal, Math.max(0, influence), _tmpTargetLocal);
                }
            } else {
                if ((r.onExit ?? "return") === "hold") {
                    this._lastFrameTime = null;
                    if (this._holdsTurn) pivotOnParentScale(animRoot, _tmpParentScale);
                    return;
                }
                // Settle early-out: once the return-to-rest completed, stop
                // writing to the node each frame — a disabled billboard must
                // not stomp rotations other systems apply while it rests.
                if (this._settled) {
                    this._lastFrameTime = null;
                    return;
                }
                // Ease back to the authored rotation (local rest = identity)
                _tmpTargetLocal.copyFrom(_identityQuat);
            }

            // Damping: frame-rate-independent smoothing toward the target.
            // Wall-clock dt (not engine delta) so behavior is testable and
            // consistent when the render loop hitches. The disengaged return
            // leg deliberately reuses the same time constant (no separate
            // returnDamping knob).
            const tau = r.damping ?? 0;
            if (tau > 0) {
                const now = performance.now();
                const dt = this._lastFrameTime === null
                    ? 0
                    : Math.min((now - this._lastFrameTime) / 1000, 0.1);
                this._lastFrameTime = now;
                const blend = 1 - Math.exp(-dt / tau);
                Quaternion.SlerpToRef(animRoot.rotationQuaternion, _tmpTargetLocal, blend, animRoot.rotationQuaternion);
            } else {
                this._lastFrameTime = null;
                animRoot.rotationQuaternion.copyFrom(_tmpTargetLocal);
            }
            this._holdsTurn = true;

            if (!engaged) {
                // Within epsilon of rest: snap exactly and latch — writes
                // stop until tracking re-engages
                const dot = Math.abs(Quaternion.Dot(animRoot.rotationQuaternion, _identityQuat));
                if (dot > 1 - SETTLE_EPSILON) {
                    animRoot.rotationQuaternion.copyFrom(_identityQuat);
                    this._settled = true;
                    this._holdsTurn = false;
                }
            }
            pivotOnParentScale(animRoot, _tmpParentScale);
        });
    }

    private _removeBillboard(): void {
        if (this._observer) {
            const scene = this.sceneObject.babylonScene;
            scene.onBeforeRenderObservable.remove(this._observer);
            this._observer = null;
        }
        this._lastFrameTime = null;
        this._settled = false;
        this._holdsTurn = false;
        this._toggle.clearPending();

        const animRoot = this.sceneObject.animationRoot;
        if (animRoot) {
            animRoot.rotationQuaternion = null;
            animRoot.setPivotMatrix(Matrix.IdentityReadOnly);
        }
    }
}
